import { formatarValor } from '@/pages/evento/utils';
import { formatarDataCurta, formatarDinheiro } from '../utils';
import { ordinal } from '../vagas';
import type { FilaLinha, InfoLinhaVagas, InscritoGerenciar } from '../types';

interface Props {
  inscrito: InscritoGerenciar;
  saldo: number | null;
  temFoto: boolean;
  fotoUrlMap: Map<number, string>;
  mostrarParticipacao: boolean;
  /** Equipe de serviço (aba Equipe do acampamento). */
  equipeMap?: Map<number, string>;
  /** Senha e vaga/suplente (aba Campistas do acampamento). */
  filaMap?: Map<number, FilaLinha>;
  /** Vaga, posição na fila, subida e cancelamento. */
  vagasMap?: Map<number, InfoLinhaVagas>;
  /** Seleção em lote (imprimir fichas). */
  selecionados?: Set<number>;
  aoAlternarSelecao?: (inscricaoId: number) => void;
  aoAbrir: (inscrito: InscritoGerenciar) => void;
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.[0] ?? '';
  const segunda = partes.length > 1 ? partes[1][0] : '';
  return (primeira + segunda).toUpperCase();
}

export default function CartaoInscritoMobile({
  inscrito,
  saldo,
  temFoto,
  fotoUrlMap,
  mostrarParticipacao,
  equipeMap,
  filaMap,
  vagasMap,
  selecionados,
  aoAlternarSelecao,
  aoAbrir,
}: Props) {
  const cancelada = inscrito.status === 'cancelada';
  const fila = filaMap?.get(inscrito.inscricao_id);
  const equipe = equipeMap?.get(inscrito.inscricao_id);
  const vagas = vagasMap?.get(inscrito.inscricao_id);

  const suplente = inscrito.status === 'lista_espera' || fila?.situacao === 'suplente';

  // Linha 1: identificação (equipe de serviço ou grupo · sexo).
  const partesIdentidade = [
    ...(vagas?.posicao ? [ordinal(vagas.posicao)] : []),
    ...(mostrarParticipacao ? [inscrito.modalidade] : []),
    ...(equipe ? [equipe] : []),
    ...(inscrito.grupo ? [inscrito.grupo] : []),
  ];
  const identidade = partesIdentidade.length ? partesIdentidade.join(' · ') : inscrito.modalidade;

  const falta = saldo !== null && !cancelada && !suplente && saldo > 0;
  const quitado = saldo !== null && !cancelada && !suplente && saldo === 0;

  // Suplente ainda não deve nada: nada de "falta R$".
  const senhaLinha = fila?.senha ?? vagas?.senha ?? null;
  const linhaSuplente = suplente
    ? `${senhaLinha ? `Senha ${senhaLinha} · ` : ''}suplente — aguardando vaga${
        vagas && !vagas.validada ? ' · não validado' : ''
      }`
    : null;
  const linhaFila =
    !suplente && senhaLinha
      ? `Senha ${senhaLinha} · ${
          fila?.situacao === 'vaga'
            ? 'vaga'
            : fila?.situacao === 'suplente'
              ? 'suplente'
              : 'aguardando'
        }`
      : null;
  const motivoCancelamento =
    cancelada && vagas?.cancelamento_motivo
      ? [vagas.cancelamento_motivo, vagas.cancelamento_detalhe].filter(Boolean).join(' · ')
      : null;

  return (
    <button
      type="button"
      onClick={() => aoAbrir(inscrito)}
      className={`flex min-h-[64px] w-full cursor-pointer items-center gap-3 rounded-3xl bg-white p-3 text-left shadow-[0_6px_18px_rgba(20,25,60,.06)] transition-transform active:scale-[0.99] ${
        cancelada ? 'opacity-60' : ''
      }`}
    >
      {aoAlternarSelecao && (
        <span
          role="checkbox"
          aria-checked={selecionados?.has(inscrito.inscricao_id) ?? false}
          aria-label={`Selecionar ${inscrito.participante}`}
          onClick={(e) => {
            e.stopPropagation();
            aoAlternarSelecao(inscrito.inscricao_id);
          }}
          className="-ml-1 flex h-9 w-8 shrink-0 cursor-pointer items-center justify-center"
        >
          <span
            className={`flex h-5 w-5 items-center justify-center rounded border ${
              selecionados?.has(inscrito.inscricao_id)
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-secondary-300 bg-white text-transparent'
            }`}
          >
            <i className="ri-check-line text-[13px]" aria-hidden />
          </span>
        </span>
      )}
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-[15px] font-bold text-primary-700">
        {temFoto && fotoUrlMap.has(inscrito.inscricao_id) ? (
          <img
            src={fotoUrlMap.get(inscrito.inscricao_id)}
            alt={`Foto de ${inscrito.participante}`}
            className="h-full w-full object-cover object-center"
          />
        ) : (
          iniciais(inscrito.participante)
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-[15px] font-semibold ${
            cancelada ? 'text-foreground-400 line-through' : 'text-foreground-950'
          }`}
        >
          {inscrito.participante}
        </span>
        <span className="block truncate text-[13px] text-foreground-500">{identidade}</span>
        {linhaSuplente ? (
          <span className="block truncate text-[12px] text-foreground-400">{linhaSuplente}</span>
        ) : (
          <span className="block text-[13px] text-foreground-500">
            {formatarValor(inscrito.valor_centavos)}
            {falta ? ` · falta ${formatarDinheiro(saldo)}` : ''}
            {quitado ? ' · quitado' : ''}
          </span>
        )}
        {linhaFila && (
          <span className="block truncate text-[12px] font-semibold text-secondary-600">
            {linhaFila}
          </span>
        )}
        {(vagas?.subiu_em || motivoCancelamento) && (
          <span className="mt-1 flex flex-wrap items-center gap-1">
            {vagas?.subiu_em && (
              <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-900">
                subiu de suplente em {formatarDataCurta(vagas.subiu_em)}
              </span>
            )}
            {motivoCancelamento && (
              <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-600">
                {motivoCancelamento}
                {vagas?.cancelada_em ? ` · ${formatarDataCurta(vagas.cancelada_em)}` : ''}
              </span>
            )}
          </span>
        )}
      </span>

      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-foreground-300">
        <i className="ri-arrow-right-s-line text-xl" />
      </span>
    </button>
  );
}